import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';

/**
 * Canonical ordering of form types.
 *
 * Design consistency is a *directed* relation, not a symmetric one. Asking
 * only "do these forms differ?" can never avoid flagging acceptable cases: if
 * 39 forms agree and one does not, a symmetric check reddens all 40, because
 * it has no notion of which is right.
 *
 * So forms are ranked. For any question, the highest-ranked form that renders
 * it defines the correct treatment, and every lower-ranked form that differs
 * is wrong. SOC leads because it is designed first and is the most important
 * form.
 *
 * The order is the `RogerDocFormType` enum order (what the form-type catalog
 * endpoint returns) bucketed by SOC / ROC / REC / DC family, which is how the
 * Forms Manager dropdown has always listed them. Keeping the one order means a
 * form sits in the same place whether you are reading the dropdown, the
 * import/export tree, the validation results, or a consistency finding.
 *
 * Note what that implies: each form is followed by its own therapy variants, so
 * `SOCPT` outranks `ROC`. A question shared by SOC and SOCPT is settled inside
 * the SOC family before any resumption-of-care form gets a say.
 */
export const FORM_PRECEDENCE: readonly RogerDocFormType[] = [
  'SOC',
  'SOCPT',
  'SOCOT',
  'SOCST',
  'ROC',
  'ROCPT',
  'ROCOT',
  'ROCST',
  'REC',
  'RECPT',
  'RECOT',
  'RECST',
  'DC',
  'DCPT',
  'DCOT',
  'DCST',
  'FUP',
  'FUPPT',
  'FUPOT',
  'FUPST',
  'TF',
  'TFPT',
  'TFOT',
  'TFST',
  'DD',
  'DDPT',
  'DDOT',
  'DDST',
  'SNAE',
  'RN',
  'RN-SVVIS',
  'RN-DCSUM',
  'SVVIS',
  'PTEVAL',
  'PTVIS',
  'PTDIS',
  'OTEVAL',
  'OTVIS',
  'OTDIS',
  'STEVAL',
  'STVIS',
  'STDIS',
  'MSWEVAL',
  'MSWVIS',
  'ACP',
  'HHAVIS',
  // Care-coordination paperwork rather than a visit note, so it sits at the
  // bottom of every list instead of in the middle of the nursing forms. It
  // also means CC60 never defines canonical for a question another form asks.
  'CC60',
  // The discharge summary restates an episode the discharge forms above have
  // already documented, so it is the last form to get a say on any question
  // they share. Its `DC` prefix is not a claim on the DC family's rank.
  'DCSUM',
  // Same for the transfer summary and the transfer forms; not in the TF family.
  'TFSUM',
];

// Keyed by plain string: callers look up form types that came off the wire and
// may not be in the enum at all.
const RANKS = new Map<string, number>(
  FORM_PRECEDENCE.map((form, index) => [form, index])
);

/**
 * Position in the canonical order; lower wins.
 *
 * Unlisted form types sort after every listed one. They are still ordered
 * among themselves by name so the choice of canonical form is deterministic
 * rather than dependent on template fetch order.
 */
export function formRank(formType: string): number {
  const rank = RANKS.get(formType);
  return rank === undefined ? FORM_PRECEDENCE.length : rank;
}

/**
 * Order two form types by precedence, then by name.
 *
 * The tiebreak matters: everything unlisted shares one rank, and a sort that
 * left those in fetch order would reshuffle the tail whenever the catalog
 * endpoint changed its mind.
 */
export function compareFormTypes(a: string, b: string): number {
  return (
    formRank(a) - formRank(b) ||
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/**
 * Catalog rows in precedence order.
 *
 * Every list of form types a user reads — the Forms Manager dropdown, the
 * import/export trees, the validation results — is sorted through here, so the
 * order on screen is the order the consistency check reasons about. Seeing SOC
 * first is a reminder that SOC defines canonical.
 */
export function sortFormTypeCatalogByPrecedence<
  T extends { form_type: string },
>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => compareFormTypes(a.form_type, b.form_type));
}

/**
 * Compare grouping keys that may include a synthetic bucket which always sorts
 * last, whatever it is named.
 *
 * The import/export tree groups rows that carry no resolvable form type under a
 * sentinel key. That bucket belongs at the end, but the rule lives here rather
 * than in the tree so there is still exactly one place that decides form order.
 */
export function compareFormGroupKeys(
  sentinelLast: string
): (a: string, b: string) => number {
  return (a, b) => {
    if (a === sentinelLast || b === sentinelLast) {
      return a === b ? 0 : a === sentinelLast ? 1 : -1;
    }
    return compareFormTypes(a, b);
  };
}

/**
 * The most canonical of a set of form types, or `null` when empty.
 *
 * Defers to `compareFormTypes` so the tiebreak rule is stated once.
 */
export function mostCanonicalForm(formTypes: readonly string[]): string | null {
  return formTypes.reduce<string | null>(
    (best, form) =>
      best === null || compareFormTypes(form, best) < 0 ? form : best,
    null
  );
}
